import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Socket } from 'socket.io-client';
import { chatService } from '../data/chat';
import { chatDb } from '../data/db';
import { Conversation, Message } from '../models/chat';
import { RootState } from '../providers/store';

export function useChat(
  conversationId?: number,
  options: { isActive?: boolean } = {},
) {
  const { isActive = false } = options;
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  const messages = useLiveQuery(
    () => {
      if (!conversationId || !currentUser) {
        return chatDb.messages.where('conversationId').equals(-1).sortBy('id');
      }
      return chatDb.messages
        .where('[conversationId+ownerId]')
        .equals([conversationId, currentUser.id])
        .sortBy('id');
    },
    [conversationId, currentUser?.id],
    [] as Message[],
  );

  const conversations = useLiveQuery(
    () => {
      if (!currentUser) return [];
      return chatDb.conversations
        .where('ownerId')
        .equals(currentUser.id)
        .toArray();
    },
    [currentUser?.id],
    [] as Conversation[],
  );

  const [users, setUsers] = useState<any[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [socket] = useState<Socket | null>(null);

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const fetchedConversationsRef = useRef(false);
  const fetchedUsersRef = useRef(false);
  const fetchedMessagesRef = useRef<Set<number>>(new Set());

  // --- Stable Callbacks ---

  const fetchConversations = useCallback(
    async (force = false) => {
      const user = currentUserRef.current;
      if (!user) return;
      if (fetchedConversationsRef.current && !force) return;

      if (!force) {
        const count = await chatDb.conversations
          .where('ownerId')
          .equals(user.id)
          .count();
        if (count > 0) {
          fetchedConversationsRef.current = true;
          return;
        }
      }

      setLoadingConversations(true);
      try {
        await chatService.listConversations(user.id);
        fetchedConversationsRef.current = true;
      } finally {
        setLoadingConversations(false);
      }
    },
    [currentUser?.id],
  );

  const fetchUsers = useCallback(async (force = false) => {
    if (fetchedUsersRef.current && !force) return;
    const data = await chatService.listUsers();
    setUsers(data);
    fetchedUsersRef.current = true;
  }, []);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await chatService.markAsRead(id);
      await chatDb.conversations.update(id, { unreadCount: 0 });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, []);

  const fetchMessages = useCallback(
    async (id: number, force = false) => {
      const user = currentUserRef.current;
      if (!user) return;
      const hasSessionFetched = fetchedMessagesRef.current.has(id);

      if (hasSessionFetched && !force) {
        const conv = await chatDb.conversations.get(id);
        if (isActiveRef.current && conv && (conv.unreadCount ?? 0) > 0) {
          markAsRead(id);
        }
        return;
      }

      if (!force && !hasSessionFetched) {
        const count = await chatDb.messages
          .where('[conversationId+ownerId]')
          .equals([id, user.id])
          .count();
        if (count > 0) {
          fetchedMessagesRef.current.add(id);

          const conv = await chatDb.conversations.get(id);
          if (isActiveRef.current && conv && (conv.unreadCount ?? 0) > 0) {
            markAsRead(id);
          }
          return;
        }
      }

      setLoadingMessages(true);
      try {
        const result = await chatService.listMessages(id, user.id);

        setNextCursor(result.nextCursor);
        const conv = await chatDb.conversations.get(id);
        if (isActiveRef.current && conv && (conv.unreadCount ?? 0) > 0) {
          markAsRead(id);
        }
        fetchedMessagesRef.current.add(id);
      } finally {
        setLoadingMessages(false);
      }
    },
    [markAsRead, currentUser?.id],
  );

  const fetchOlderMessages = useCallback(async () => {
    const user = currentUserRef.current;
    if (!conversationId || !nextCursor || loadingOlder || !user) return;

    setLoadingOlder(true);
    try {
      const result = await chatService.listMessages(
        conversationId,
        user.id,
        nextCursor,
      );
      setNextCursor(result.nextCursor);
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, nextCursor, loadingOlder, currentUser?.id]);

  const sendMessage = useCallback(
    async (content: string) => {
      const currentUser = currentUserRef.current;
      if (!conversationId || !currentUser) return;

      const optimisticMsg: Message = {
        id: Date.now(),
        content,
        sender: currentUser,
        conversationId: Number(conversationId),
        createdAt: new Date().toISOString(),
        username: currentUser.username,
        edited: false,
        ownerId: currentUser.id,
      };

      // Put optimistic directly into Dexie
      await chatDb.messages.put(optimisticMsg);

      try {
        await chatService.sendMessage(
          Number(conversationId),
          content,
          currentUser.id,
        );
        // Delete optimistic and trust the one coming from backend (since chatService puts it in Dexie)
        await chatDb.messages.delete(optimisticMsg.id);
      } catch (error) {
        await chatDb.messages.delete(optimisticMsg.id);
        console.error('Failed to send message:', error);
      }
    },
    [conversationId, currentUser?.id],
  );

  const startConversation = useCallback(async (userId: number) => {
    const conversation = await chatService.createPrivateConversation(userId);
    return conversation;
  }, []);

  const createGroupConversation = useCallback(
    async (name: string, participantIds: number[]) => {
      const conversation = await chatService.createGroupConversation(
        name,
        participantIds,
      );
      return conversation;
    },
    [],
  );

  // --- Socket Initialization ---

  useEffect(() => {
    // Websocket connections disabled for testing
    return;
  }, [currentUser?.id, fetchConversations]);

  useEffect(() => {
    if (!socket || !conversationId) return;

    const currentConvId = conversationId.toString();

    const join = () => {
      socket.emit('joinRoom', currentConvId);
    };

    if (socket.connected) {
      join();
    }
    socket.on('connect', join);

    return () => {
      socket.off('connect', join);
      socket.emit('leaveRoom', currentConvId);
    };
  }, [socket, conversationId]);

  useEffect(() => {
    if (!conversationId) {
      setNextCursor(null);
    }
  }, [conversationId]);

  useEffect(() => {
    if (isActive && conversationId) {
      chatDb.conversations.get(conversationId).then((conv) => {
        if (conv && (conv.unreadCount ?? 0) > 0) {
          markAsRead(conversationId);
        }
      });
    }
  }, [isActive, conversationId, markAsRead]);

  return {
    messages: messages || [],
    conversations,
    users,
    nextCursor,
    loadingOlder,
    loadingMessages,
    loadingConversations,
    sendMessage,
    markAsRead,
    startConversation,
    createGroupConversation,
    fetchMessages,
    fetchOlderMessages,
    fetchConversations,
    fetchUsers,
    socket,
  };
}
